"use client";

/**
 * ErpMegaMenu — topbar mega meni 820px, 4 kolone (1:1 mockup port).
 *
 * Mockup: docs/mockups/erp-skeleton-v1-popunjen.html (lin 892-973).
 * Trigger: "Operacije ▼" u tb__nav.
 *
 * Pravila:
 *  - PRAVILO 11: KLIK otvara, klik-van zatvara, ESC zatvara (NIKAD hover)
 *  - PRAVILO 13: ne ugrožava sveta zona desno
 *  - 4 kolone: Prodaja · Magacin · Finansije · Promo
 */

import * as React from "react";
import Link from "next/link";
import {
  FileText,
  Receipt,
  Coins,
  Users,
  Package,
  Inbox,
  Send,
  ClipboardCheck,
  ChevronDown,
} from "@grozd/icons";
import type { LucideIcon } from "@grozd/icons";
import { isHrefEnabled } from "@/lib/flags";

interface MegaLink {
  label: string;
  meta?: string;
  href: string;
  icon?: LucideIcon;
  badge?: string;
}

interface MegaColumn {
  title: string;
  links: MegaLink[];
  compact?: boolean;
}

const COLUMNS: MegaColumn[] = [
  {
    title: "Prodaja",
    links: [
      { label: "Fakture", meta: "Ulazne, izlazne, proforma", href: "/fakture", icon: FileText },
      { label: "Ponude", meta: "Predračuni i ugovori", href: "/fakture/predracuni", icon: Receipt },
      { label: "Naplata", meta: "Otvorene stavke i opomene", href: "/fakture?status=otvoreno", icon: Coins, badge: "12" },
      { label: "Kupci", meta: "CRM + kontakt registar", href: "/partneri?tip=kupac", icon: Users },
    ],
  },
  {
    title: "Magacin",
    links: [
      { label: "Robe i usluge", meta: "Šifarnik i stanja", href: "/artikli", icon: Package },
      { label: "Prijemnice", meta: "Ulaz robe", href: "/magacin/prijemnica/nova", icon: Inbox },
      { label: "Otpremnice", meta: "Izlaz robe", href: "/magacin/otpremnica/nova", icon: Send },
      { label: "Inventar", meta: "Popis i razlike", href: "/magacin/popis", icon: ClipboardCheck },
    ],
  },
  {
    title: "Finansije",
    compact: true,
    links: [
      { label: "Banka", meta: "Izvodi i nalozi za plaćanje", href: "/banka" },
      { label: "Računovodstvo", meta: "Knjiženja i bilans", href: "/racunovodstvo" },
      { label: "Plate", meta: "PIO/PUCG prijave", href: "/plate" },
      { label: "Bonitet", meta: "Ocjena tvoje firme", href: "/bonitet", badge: "Novo" },
      { label: "Devizni izvještaji", meta: "CBCG izvještavanje", href: "/cbcg" },
    ],
  },
];

/**
 * PILOT filter — sakriva van-pilot linkove (magacin, banka, računovodstvo,
 * plate) iz mega menija. Kolone koje ostanu prazne se izbacuju; "Finansije"
 * kolona miješa keep/hide linkove pa se filtrira po linku, ne po koloni.
 */
const VISIBLE_COLUMNS: MegaColumn[] = COLUMNS.map((col) => {
  const links = col.links.filter((link) => isHrefEnabled(link.href));
  // F7 kozmetika (dug iz IMPL-F1): u pilotu "Magacin" kolona preživi samo
  // sa šifarnikom artikala — naslov "Magacin" bi zbunjivao (modul je van
  // pilota). Preimenuj kolonu kad su magacin linkovi odfiltrirani.
  const title =
    col.title === "Magacin" && links.length < col.links.length
      ? "Šifarnik"
      : col.title;
  return { ...col, title, links };
}).filter((col) => col.links.length > 0);

interface ErpMegaMenuProps {
  /** Da li je meni otvoren */
  open: boolean;
  /** Callback kad korisnik traži zatvaranje (klik van, ESC) */
  onClose: () => void;
}

export function ErpMegaMenu({ open, onClose }: ErpMegaMenuProps) {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    // Use capture: false, mousedown so trigger toggle still works (trigger is outside ref)
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={ref}
      role="menu"
      aria-label="Mega meni — Operacije"
      style={{
        position: "absolute",
        top: "calc(100% + 6px)",
        left: -12,
        background: "var(--surface-card)",
        border: "1px solid var(--n100)",
        borderRadius: 18,
        boxShadow: "0 12px 40px rgba(45,27,105,0.18)",
        overflow: "hidden",
        width: 820,
        display: "flex",
        zIndex: 60,
        animation: "erpMegaIn 200ms cubic-bezier(0.22,1,0.36,1)",
      }}
    >
      {VISIBLE_COLUMNS.map((col) => (
        <div
          key={col.title}
          style={{
            flex: 1,
            padding: "18px 0 14px",
            borderRight: "1px solid var(--n100)",
          }}
        >
          <div
            className="font-brand"
            style={{
              fontSize: 10.5,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--n400)",
              marginBottom: 6,
              padding: "0 16px",
            }}
          >
            {col.title}
          </div>
          {col.links.map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.href + link.label}
                href={link.href}
                onClick={onClose}
                role="menuitem"
                className="erp-mega__link"
                style={{
                  margin: "0 8px",
                  padding: col.compact ? "6px 14px" : "8px 10px",
                  borderRadius: 10,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  textDecoration: "none",
                  color: "inherit",
                  transition: "background 120ms",
                }}
              >
                {Icon && (
                  <span
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 6,
                      background: "var(--p100)",
                      color: "var(--primary)",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={16} strokeWidth={2} />
                  </span>
                )}
                <span style={{ flex: 1, minWidth: 0 }}>
                  <strong
                    style={{
                      fontSize: 13,
                      color: "var(--ink)",
                      fontWeight: 600,
                      display: "block",
                    }}
                  >
                    {link.label}
                  </strong>
                  {link.meta && (
                    <span
                      style={{
                        fontSize: 11.5,
                        color: "var(--n400)",
                        display: "block",
                        marginTop: 1,
                      }}
                    >
                      {link.meta}
                    </span>
                  )}
                </span>
                {link.badge && (
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: 10,
                      fontWeight: 700,
                      padding: "2px 7px",
                      borderRadius: 6,
                      background:
                        link.badge === "Novo"
                          ? "linear-gradient(135deg,#FBBF24,#F97316)"
                          : "var(--p100)",
                      color: link.badge === "Novo" ? "#fff" : "var(--primary)",
                      flexShrink: 0,
                    }}
                  >
                    {link.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      ))}

      {/* Promo column */}
      <div
        style={{
          flex: "0 0 220px",
          background: "linear-gradient(135deg, var(--p100), var(--tint))",
          padding: 18,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div
            className="font-brand"
            style={{
              fontWeight: 800,
              fontSize: 14,
              color: "var(--primary)",
              marginBottom: 6,
            }}
          >
            Bonitet Grozd
          </div>
          <div
            className="font-body"
            style={{
              fontSize: 12.5,
              color: "var(--n600)",
              lineHeight: 1.5,
              marginBottom: 14,
            }}
          >
            Prva crnogorska bonitetna ocjena za firme. EFI · eFakture · CBCG ·
            sve u jednoj cifri.
          </div>
        </div>
        <Link
          href="/bonitet"
          onClick={onClose}
          className="font-brand"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "8px 12px",
            borderRadius: 10,
            background: "var(--primary)",
            color: "#fff",
            fontWeight: 600,
            fontSize: 12.5,
            cursor: "pointer",
            textDecoration: "none",
            alignSelf: "flex-start",
            transition: "all 120ms",
          }}
        >
          Saznaj više <ChevronDown size={14} style={{ transform: "rotate(-90deg)" }} />
        </Link>
      </div>

      <style jsx>{`
        @keyframes erpMegaIn {
          from {
            opacity: 0;
            transform: translateY(-6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        :global(.erp-mega__link):hover {
          background: var(--tint) !important;
        }
      `}</style>
    </div>
  );
}

export default ErpMegaMenu;
